import React, { useState } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import { Link, useSearchParams } from "react-router-dom";
import Approvals from "./Approvals";
import Sidebar from "../Sidebar";
import Layout from "../../layout/Layout";

// Staff desk: where the day starts.
//   Needs action  pending leaves, questioned deductions, leaves with
//                 bookings, missing documents, stock requests, payroll
//   Data clean-up duplicate records and records whose data makes pay or
//                 leave wrong (no joining date, no salary, ...)

const GET_ADMIN_INBOX = gql`
  query GetAdminInbox {
    getAdminInbox {
      status
      message
      inbox {
        total
        sections {
          key
          title
          link
          count
          items {
            id
            title
            subtitle
            date
            urgent
            link
          }
        }
      }
    }
  }
`;

const GET_STAFF_DATA_ISSUES = gql`
  query GetStaffDataIssues {
    getStaffDataIssues {
      status
      message
      report {
        activeCount
        duplicates {
          matchedOn
          records {
            employeeId
            fullName
            contactNumber
            active
            takesBookings
            isOnBoarded
            isDisabled
            createdAt
            orders
            leaves
            fines
          }
        }
        issues {
          employeeId
          fullName
          code
          message
        }
      }
    }
  }
`;

const WAIVE_LEAVE_DOCUMENT = gql`
  mutation WaiveLeaveDocument($leaveId: ID!) {
    waiveLeaveDocument(leaveId: $leaveId) {
      status
      message
    }
  }
`;

const SECTION_HINT = {
  PENDING_LEAVES: "Leave Management → List",
  DISPUTES: "Leave Management → Disputes",
  BOOKING_CONFLICTS: "Leave Management → Calendar shows the clashes",
  MISSING_DOCUMENTS: "The professional adds it from the app (Leave → Add document), or mark it Not needed",
  STOCK_REQUESTS: "Open the professional's dashboard",
  PAYROLL: "Payouts → Check & Lock",
};

const ISSUE_LABEL = {
  NO_JOINING_DATE: "No joining date",
  NO_PAY_TYPE: "No pay type",
  ZERO_SALARY: "Salary ₹0",
  LEFT_BUT_ACTIVE: "Left but employed",
  NO_PUSH_TOKEN: "No notifications",
  NO_LEAVING_DATE: "Left, no leaving date",
  LEFT_TAKING_BOOKINGS: "Left but bookable",
};
const ISSUE_FIX = {
  NO_JOINING_DATE: "Set it in User Management → Edit (Joining Date)",
  NO_PAY_TYPE: "Set Salary Type in User Management → Edit",
  ZERO_SALARY: "Set the salary in User Management → Edit",
  LEFT_BUT_ACTIVE: "User Management → switch Employed off (marks them as left)",
  NO_LEAVING_DATE: "User Management → Edit → Leaving Date, or switch Employed back on if they still work here",
  LEFT_TAKING_BOOKINGS: "User Management → switch Dummy on (kept open for bookings) or Bookings off",
  NO_PUSH_TOKEN: "Ask them to open the app and log in again",
};

const fmtDate = (d) =>
  d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "";

const NeedsAction = () => {
  const { data, loading, refetch } = useQuery(GET_ADMIN_INBOX, { fetchPolicy: "network-only" });
  const [waiveDocument] = useMutation(WAIVE_LEAVE_DOCUMENT);
  // The office decides a leave doesn't need its document after all
  const notNeeded = async (leaveId) => {
    if (!window.confirm("Mark the document as not needed for this leave?")) return;
    const out = (await waiveDocument({ variables: { leaveId } })).data?.waiveLeaveDocument;
    if (out?.status !== "SUCCESS") alert(out?.message || "Failed");
    refetch();
  };
  const res = data?.getAdminInbox;
  const sections = res?.inbox?.sections || [];
  if (loading) return <p className="text-sm text-gray-500">Loading...</p>;
  if (res?.status === "ERROR") return <p className="text-sm text-red-600">{res.message}</p>;
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-600">
          {res.inbox.total === 0 ? "Nothing waiting. 🎉" : `${res.inbox.total} things waiting, most urgent at the top of each list.`}
        </p>
        <button onClick={() => refetch()} className="text-sm text-gray-600 hover:text-gray-900 font-semibold">
          Refresh
        </button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {sections.map((s) => (
          <div key={s.key} className={`border rounded-lg ${s.count ? "" : "opacity-60"}`}>
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
              <div>
                <span className="font-semibold text-gray-800">{s.title}</span>
                <span
                  className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                    s.count ? "bg-[#FBB033] text-black" : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {s.count}
                </span>
              </div>
              {s.link && (
                <Link to={s.link} className="text-xs font-semibold text-blue-600 hover:text-blue-800">
                  Open →
                </Link>
              )}
            </div>
            {s.count === 0 ? (
              <p className="px-4 py-3 text-sm text-gray-400">All clear</p>
            ) : (
              <ul className="divide-y max-h-72 overflow-y-auto">
                {s.items.map((i) => {
                  const body = (
                    <>
                      <div className="flex justify-between gap-2">
                        <span className="font-medium text-gray-800">
                          {i.urgent && <span className="text-red-600 mr-1" title="Urgent">●</span>}
                          {i.title}
                        </span>
                        <span className="text-xs text-gray-400 whitespace-nowrap">{fmtDate(i.date)}</span>
                      </div>
                      <div className="text-xs text-gray-600">{i.subtitle}</div>
                    </>
                  );
                  return (
                    <li key={i.id} className="px-4 py-2 text-sm">
                      {i.link ? (
                        <Link to={i.link} className="block hover:bg-gray-50">
                          {body}
                        </Link>
                      ) : (
                        body
                      )}
                      {s.key === "MISSING_DOCUMENTS" && (
                        <button
                          onClick={() => notNeeded(i.id)}
                          className="mt-1 text-xs font-semibold text-blue-600 hover:text-blue-800"
                        >
                          Not needed
                        </button>
                      )}
                    </li>
                  );
                })}
                {s.count > s.items.length && (
                  <li className="px-4 py-2 text-xs text-gray-500">+{s.count - s.items.length} more</li>
                )}
              </ul>
            )}
            {s.count > 0 && SECTION_HINT[s.key] && (
              <div className="px-4 py-2 border-t text-[11px] text-gray-500">Where: {SECTION_HINT[s.key]}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const DataCleanup = () => {
  const { data, loading, refetch } = useQuery(GET_STAFF_DATA_ISSUES, { fetchPolicy: "network-only" });
  const res = data?.getStaffDataIssues;
  const [filter, setFilter] = useState("ALL");
  if (loading) return <p className="text-sm text-gray-500">Loading...</p>;
  if (res?.status === "ERROR") return <p className="text-sm text-red-600">{res.message}</p>;
  const { duplicates, issues, activeCount } = res.report;
  const codes = [...new Set(issues.map((i) => i.code))];
  const shown = issues.filter((i) => filter === "ALL" || i.code === filter);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {activeCount} active professionals · {duplicates.length} possible duplicate
          {duplicates.length === 1 ? "" : "s"} · {issues.length} data issue{issues.length === 1 ? "" : "s"}
        </p>
        <button onClick={() => refetch()} className="text-sm text-gray-600 hover:text-gray-900 font-semibold">
          Refresh
        </button>
      </div>

      <div className="border rounded-lg">
        <div className="px-4 py-3 border-b bg-gray-50">
          <span className="font-semibold text-gray-800">Possible duplicates</span>
          <p className="text-xs text-gray-500">
            Same phone number or same name. Bookings, leaves and fines split across records, so keep the one with the
            history and disable the others. Merging records is not automatic.
          </p>
        </div>
        {duplicates.length === 0 ? (
          <p className="px-4 py-3 text-sm text-gray-400">None found</p>
        ) : (
          duplicates.map((g, gi) => (
            <div key={gi} className="px-4 py-3 border-b last:border-b-0">
              <div className="text-xs text-gray-500 mb-2">
                Matched on {g.matchedOn === "PHONE" ? "phone number" : "name (check it's really the same person)"}
              </div>
              <table className="min-w-full text-left text-sm text-gray-600">
                <thead className="text-gray-500 text-xs">
                  <tr>
                    <th className="py-1 pr-3">Name</th>
                    <th className="py-1 pr-3">Phone</th>
                    <th className="py-1 pr-3">Status</th>
                    <th className="py-1 pr-3">Created</th>
                    <th className="py-1 pr-3 text-right">Bookings</th>
                    <th className="py-1 pr-3 text-right">Leaves</th>
                    <th className="py-1 pr-3 text-right">Fine days</th>
                    <th className="py-1 pr-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {g.records.map((r) => (
                    <tr key={r.employeeId}>
                      <td className="py-1 pr-3 text-gray-800">{r.fullName}</td>
                      <td className="py-1 pr-3">{r.contactNumber}</td>
                      <td className="py-1 pr-3">
                        <span
                          className={`px-2 py-0.5 rounded text-[11px] font-semibold ${
                            r.active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {r.active ? "Employed" : "Left"}
                          {r.takesBookings ? " · bookable" : ""}
                        </span>
                      </td>
                      <td className="py-1 pr-3">{r.createdAt}</td>
                      <td className="py-1 pr-3 text-right">{r.orders}</td>
                      <td className="py-1 pr-3 text-right">{r.leaves}</td>
                      <td className="py-1 pr-3 text-right">{r.fines}</td>
                      <td className="py-1 pr-3">
                        <Link
                          to={`/admin/employee-dashboard/${r.employeeId}`}
                          className="text-xs font-semibold text-blue-600 hover:text-blue-800"
                        >
                          Open
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        )}
      </div>

      <div className="border rounded-lg">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b bg-gray-50">
          <span className="font-semibold text-gray-800">Records that make pay or leave wrong</span>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="border rounded px-2 py-1 text-sm">
            <option value="ALL">All issues ({issues.length})</option>
            {codes.map((c) => (
              <option key={c} value={c}>
                {ISSUE_LABEL[c] || c} ({issues.filter((i) => i.code === c).length})
              </option>
            ))}
          </select>
        </div>
        {shown.length === 0 ? (
          <p className="px-4 py-3 text-sm text-gray-400">None</p>
        ) : (
          <table className="min-w-full text-left text-sm text-gray-600">
            <tbody className="divide-y">
              {shown.map((i) => (
                <tr key={`${i.employeeId}-${i.code}`}>
                  <td className="py-2 px-4 text-gray-800 whitespace-nowrap">{i.fullName}</td>
                  <td className="py-2 px-4">
                    <span className="px-2 py-0.5 rounded text-[11px] font-semibold bg-yellow-100 text-yellow-800 whitespace-nowrap">
                      {ISSUE_LABEL[i.code] || i.code}
                    </span>
                  </td>
                  <td className="py-2 px-4">
                    {i.message}
                    <div className="text-xs text-gray-400">{ISSUE_FIX[i.code]}</div>
                  </td>
                  <td className="py-2 px-4">
                    <Link
                      to={`/admin/employee-dashboard/${i.employeeId}`}
                      className="text-xs font-semibold text-blue-600 hover:text-blue-800"
                    >
                      Open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const StaffDesk = () => {
  // ?tab=approvals opens that tab (links from the inbox)
  const [params] = useSearchParams();
  const [tab, setTab] = useState(() => (["action", "approvals", "cleanup"].includes(params.get("tab")) ? params.get("tab") : "action"));
  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="p-6 bg-white w-full rounded-lg shadow-md m-4 overflow-y-auto">
          <div className="flex flex-wrap justify-between gap-3 mb-6 border-b pb-4">
            <h1 className="text-2xl font-semibold text-gray-800">Staff Desk</h1>
            <div className="flex rounded border overflow-hidden text-sm">
              {[
                ["action", "Needs action"],
                ["approvals", "Approvals"],
                ["cleanup", "Data clean-up"],
              ].map(([k, label]) => (
                <button
                  key={k}
                  onClick={() => setTab(k)}
                  className={`px-3 py-2 ${tab === k ? "bg-gray-800 text-white" : "bg-white text-gray-700 hover:bg-gray-100"}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          {tab === "action" && <NeedsAction />}
          {tab === "approvals" && <Approvals />}
          {tab === "cleanup" && <DataCleanup />}
        </div>
      </Layout>
    </div>
  );
};

export default StaffDesk;
